import { useEffect } from 'react';

type Options = {
  edgeWidth?: number;   // ширина активной зоны от левого края (px)
  minDeltaX?: number;   // минимальный горизонтальный сдвиг для срабатывания (px)
  maxDeltaY?: number;   // допустимое вертикальное отклонение (px)
};

/**
 * Универсальный хук свайпа-назад от левого края экрана.
 * Не конфликтует со скроллом: игнорирует жесты с большим вертикальным отклонением.
 */
export function useSwipeBack(
  ref: React.RefObject<HTMLElement>,
  onBack: () => void,
  { edgeWidth = 30, minDeltaX = 70, maxDeltaY = 40 }: Options = {}
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      // Срабатываем только если жест начался у левого края
      if (t.clientX > edgeWidth) return;
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);

      // Если пользователь уходит в вертикальный скролл — отменяем
      if (dy > maxDeltaY) {
        tracking = false;
        return;
      }

      // Достаточный сдвиг вправо — выполняем onBack
      if (dx > minDeltaX) {
        tracking = false;
        onBack();
      }
    };

    const onEnd = () => { tracking = false; };
    const onCancel = () => { tracking = false; };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [ref, onBack, edgeWidth, minDeltaX, maxDeltaY]);
}


